/**
 * Part editors for Edit Workspace Homepage. Aligned to the business homepage
 * config model (single source of truth):
 *   Part1Editor — Hero title as colored SEGMENTS + subheading.
 *   Part2Editor — Hot Questions (UI copy + prompt + NEW/HOT badges, add/remove,
 *                 reorder by drag or up/down).
 *   Part3Editor — Hot Topics (icon + title + description + prompt + badges +
 *                 click action: send prompt to chat, or navigate to a link;
 *                 add/remove, reorder by drag or up/down).
 * Each is a controlled editor: it holds a working copy and calls onPublish /
 * onCancel; the parent persists via homepageConfigClient.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import Checkbox from '@amzn/awsui-components-react/polaris/checkbox';
import Input from '@amzn/awsui-components-react/polaris/input';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Textarea from '@amzn/awsui-components-react/polaris/textarea';
import type { HomepageBadge, HotQuestion, HotTopic, Part1Config, TitleSegment } from './types';
import { newQuestionId, newTopicId } from './homepageConfigClient';
import { ColorPopover } from './ColorPopover';
import { appConfig } from '../../config';
import { gatewayAuthHeaders } from '../../context/gatewayAuthHeaders';
import { isAllowedHref, resolveIconSrc } from '../../components/WorkspaceHome/homepageConfig';

/** Max number of hot questions / hot topics per agent. */
const MAX_ITEMS = 12;

/** Cancel(link) + Publish(orange primary) footer, shared by all Part editors. */
function EditorFooter({
  onCancel,
  onPublish,
  publishDisabled,
}: {
  onCancel: () => void;
  onPublish: () => void;
  publishDisabled?: boolean;
}) {
  const { t } = useTranslation('settings');
  return (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={onCancel}>{t('homepageEditor.cancel')}</Button>
        <span className="rbms-primary-btn">
          <Button variant="primary" onClick={onPublish} disabled={publishDisabled}>{t('homepageEditor.publish')}</Button>
        </span>
      </SpaceBetween>
    </Box>
  );
}

/** Full-width labeled field (replaces Cloudscape FormField, which caps the
 *  control width; Figma fields fill the whole card). */
function Field({
  label,
  hint,
  optional,
  error,
  children,
}: {
  label: string;
  hint?: string;
  /** Appends a "- optional" suffix to the label. */
  optional?: boolean;
  /** Validation message; shown in place of the hint. */
  error?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation('settings');
  return (
    <div className="rbms-ewh-field">
      <div className="rbms-ewh-field__label">
        {label}
        {optional ? <span className="rbms-ewh-field__optional"> {t('homepageEditor.optional')}</span> : null}
      </div>
      {children}
      {error ? (
        <div className="rbms-ewh-field__error" role="alert">{error}</div>
      ) : hint ? (
        <div className="rbms-ewh-field__hint">{hint}</div>
      ) : null}
    </div>
  );
}

/** Card header row: "Part N" title on the left + optional action (e.g. the Add
 *  button) on the right, same line (Figma). The editor renders this while
 *  editing, since the section's own head is hidden then. */
function EditorHead({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="rbms-ewh-ehead">
      <span className="rbms-ewh-section__title">{title}</span>
      {action}
    </div>
  );
}

// ── Bilingual fields (Chinese base text beside the optional English) ────────

/** One text field in both languages side by side: the Chinese base text on the
 *  left, its optional English on the right (one column on narrow screens), so
 *  each language is typed into its own box and neither can overwrite the other. */
function BilingualField({
  label,
  hint,
  error,
  zh,
  en,
}: {
  label: string;
  hint?: string;
  error?: string;
  zh: ReactNode;
  en: ReactNode;
}) {
  const { t: tCommon } = useTranslation('common');
  return (
    <Field label={label} hint={hint} error={error}>
      <div className="rbms-ewh-bi">
        <div className="rbms-ewh-bi__col">
          <div className="rbms-ewh-bi__lang" lang="zh-CN">{tCommon('language.zh')}</div>
          {zh}
        </div>
        <div className="rbms-ewh-bi__col">
          <div className="rbms-ewh-bi__lang" lang="en">{tCommon('language.en')}</div>
          {en}
        </div>
      </div>
    </Field>
  );
}

/** Accessible names for a bilingual field's two boxes, e.g. "Prompt (中文)". */
function useBilingualLabels() {
  const { t: tCommon } = useTranslation('common');
  return (label: string) => ({
    zh: `${label} (${tCommon('language.zh')})`,
    en: `${label} (${tCommon('language.en')})`,
  });
}

/** The once-per-Part note that English is optional. */
function EnglishHint() {
  const { t } = useTranslation('settings');
  return <div className="rbms-ewh-field__hint">{t('homepageEditor.englishHint')}</div>;
}

/** Drop English fields left empty, and `en` itself when nothing is left, so the
 *  homepage falls back to the Chinese text there. */
function withoutEmptyEnglish<T extends { en?: object }>(item: T): T {
  if (!item.en) return item;
  const en = Object.fromEntries(
    Object.entries(item.en).filter(([, v]) => (typeof v === 'string' ? v.trim() !== '' : Array.isArray(v) && v.length > 0)),
  );
  const { en: _dropped, ...rest } = item;
  return (Object.keys(en).length ? { ...rest, en } : rest) as T;
}

/** The text segments typed into a rich-text field, without empty runs or the
 *  line breaks left at the end (the browser keeps a placeholder <br> there,
 *  which the homepage would otherwise show as an empty last line). */
function editedSegments(ref: RefObject<HTMLDivElement>): TitleSegment[] {
  if (!ref.current) return [];
  const segs = parseSegments(ref.current).filter((s) => s.text.length > 0);
  while (segs.length) {
    const last = segs[segs.length - 1];
    const text = last.text.replace(/\n+$/, '');
    if (text) {
      segs[segs.length - 1] = { ...last, text };
      break;
    }
    segs.pop();
  }
  return segs;
}

/** Number of lines in the given segments ("\n" separates lines). */
function lineCount(segs: TitleSegment[]): number {
  return segs.map((s) => s.text).join('').split('\n').length;
}

/** Lines currently in an editable, not counting the placeholder <br> the browser
 *  keeps at the end of the last line (so "abc" + Enter counts as 2, not 3). */
function editableLineCount(el: HTMLElement): number {
  const text = parseSegments(el).map((s) => s.text).join('');
  return text.replace(/\n$/, '').split('\n').length;
}

// ── Part 1 (rich-text title + subheading, per Figma) ────────────────────────

const WEIGHT_OPTIONS = [
  { value: 'bold', labelKey: 'homepageEditor.weightBold' },
  { value: 'regular', labelKey: 'homepageEditor.weightRegular' },
] as const;

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Normalize a CSS color (hex or rgb()) to lowercase #rrggbb; undefined if none. */
function toHex(color: string): string | undefined {
  if (!color) return undefined;
  if (color.startsWith('#')) return color.toLowerCase();
  const m = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return undefined;
  const h = (n: string) => Number(n).toString(16).padStart(2, '0');
  return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
}

/** Seed the editable's initial HTML from stored segments (colored spans).
 *  This is the one raw-innerHTML path (the React renderer is safe on its own),
 *  so the color is normalized through toHex — anything that isn't a #hex / rgb()
 *  value yields no color attribute, preventing style-attribute injection (XSS)
 *  even if a malformed color slips past backend validation. */
function segmentsToHtml(segs: TitleSegment[]): string {
  return segs
    .map((s) => {
      const hex = s.color ? toHex(s.color) : undefined;
      return hex ? `<span style="color:${hex}">${escapeHtml(s.text)}</span>` : escapeHtml(s.text);
    })
    .join('');
}

/** Parse the contentEditable DOM back into color segments, merging adjacent
 *  runs of the same color. Text nodes → uncolored; colored spans → {text,color}. */
function parseSegments(root: HTMLElement): TitleSegment[] {
  const segs: TitleSegment[] = [];
  const push = (text: string, color?: string) => {
    if (!text) return;
    const last = segs[segs.length - 1];
    if (last && (last.color ?? '') === (color ?? '')) last.text += text;
    else segs.push(color ? { text, color } : { text });
  };
  // contentEditable represents line breaks as <br> or block elements (<div>/<p>);
  // emit "\n" for them so multiline text (subheading) survives publish.
  const BLOCK = new Set(['DIV', 'P']);
  const endsWithNewline = () => {
    const last = segs[segs.length - 1];
    return !!last && last.text.endsWith('\n');
  };
  const walk = (node: Node, inherited?: string) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        push(child.textContent ?? '', inherited);
      } else if (child instanceof HTMLElement) {
        if (child.tagName === 'BR') {
          push('\n', inherited);
          return;
        }
        // A block element starts a new visual line (except the very first content).
        if (BLOCK.has(child.tagName) && segs.length && !endsWithNewline()) {
          push('\n', inherited);
        }
        const c = toHex(child.style.color) ?? toHex(child.getAttribute('color') ?? '') ?? inherited;
        walk(child, c);
      }
    });
  };
  walk(root);
  return segs;
}

/** Figma rich-text toolbar: weight dropdown | Color picker | link icon. Weight
 *  and link are display-only (the homepage renderer fixes the title weight and
 *  the title has no link in the config model); Color drives the segment colors. */
function EditorToolbar({
  weight,
  onWeight,
  color,
  onColor,
  baseColor,
  disabled = false,
}: {
  weight: string;
  onWeight: (w: 'bold' | 'regular') => void;
  color: string;
  onColor: (hex: string) => void;
  baseColor: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation('settings');
  return (
    <div className="rbms-ewh-rte__bar">
      <select
        className="rbms-ewh-rte__select"
        value={weight}
        disabled={disabled}
        onChange={(e) => onWeight(e.target.value as 'bold' | 'regular')}
        aria-label={t('homepageEditor.fontWeight')}
      >
        {WEIGHT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
        ))}
      </select>
      <ColorPopover value={color} onChange={onColor} baseColor={baseColor} disabled={disabled} />
      <button type="button" className="rbms-ewh-rte__link" disabled title={t('homepageEditor.linkDisabled')}>
        {/* Horizontal chain-link (Lucide link-2), matching Figma. */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 17H7A5 5 0 0 1 7 7h2" />
          <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      </button>
    </div>
  );
}

/** Rich text with per-run color: select text in the box, pick a Color to tint
 *  that run. On publish the parent reads segments off the editable via
 *  `editableRef`. Used for both the title and the subheading; both are multiline
 *  (Enter starts a new line, stored as "\n" and shown as a line break), and the
 *  title is capped at TITLE_MAX_LINES. */
function RichTextField({
  initial,
  editableRef,
  multiline = false,
  maxLines,
  onInput,
  defaultWeight = 'bold',
  seedColor = '#0097a7',
  baseColor = '#0f141a',
}: {
  initial: TitleSegment[];
  editableRef: RefObject<HTMLDivElement>;
  multiline?: boolean;
  /** Multiline only: Enter stops adding lines at this many, and a paste turns
   *  the line breaks that would go past it into spaces. */
  maxLines?: number;
  onInput?: () => void;
  defaultWeight?: 'bold' | 'regular';
  seedColor?: string;
  /** Inherited text color, applied when the user picks "None". */
  baseColor?: string;
}) {
  const [weight, setWeight] = useState<'bold' | 'regular'>(defaultWeight);
  const [color, setColor] = useState(initial.find((s) => s.color)?.color || seedColor);

  // Seed once from the initial segments (uncontrolled to keep the caret stable).
  useEffect(() => {
    if (editableRef.current) editableRef.current.innerHTML = segmentsToHtml(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyColor = (hex: string) => {
    setColor(hex);
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    // Emit inline `style="color:"` (not the deprecated <font color>).
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand('foreColor', false, hex);
  };

  return (
    <div className="rbms-ewh-rte">
      <EditorToolbar
        weight={weight}
        onWeight={setWeight}
        color={color}
        onColor={applyColor}
        baseColor={baseColor}
      />
      <div
        ref={editableRef}
        className={`rbms-ewh-rte__area${multiline ? ' rbms-ewh-rte__area--multiline' : ''}`}
        contentEditable
        suppressContentEditableWarning
        style={{ fontWeight: weight === 'bold' ? 700 : 400 }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          if (!multiline) e.preventDefault(); // single-line field
          else if (maxLines && editableLineCount(e.currentTarget) >= maxLines) e.preventDefault();
        }}
        onPaste={(e) => {
          if (!maxLines) return;
          // Paste as plain text, keeping only the line breaks that still fit.
          e.preventDefault();
          let room = maxLines - editableLineCount(e.currentTarget);
          const text = e.clipboardData
            .getData('text/plain')
            .replace(/\r\n?/g, '\n')
            .replace(/\n/g, () => (room-- > 0 ? '\n' : ' '));
          document.execCommand('insertText', false, text);
        }}
        onInput={onInput}
      />
    </div>
  );
}

/** The hero title may break onto at most this many lines. */
const TITLE_MAX_LINES = 2;

export function Part1Editor({
  value,
  onCancel,
  onPublish,
}: {
  value: Part1Config;
  onCancel: () => void;
  onPublish: (v: Part1Config) => void;
}) {
  const { t } = useTranslation('settings');
  // One editable per line and language, shown side by side; each is seeded once
  // and uncontrolled.
  const refs = {
    zh: { title: useRef<HTMLDivElement>(null), sub: useRef<HTMLDivElement>(null) },
    en: { title: useRef<HTMLDivElement>(null), sub: useRef<HTMLDivElement>(null) },
  };
  // Seed from segments; fall back to the single-string value for legacy data.
  const seed = (segs?: TitleSegment[], text?: string): TitleSegment[] => (segs?.length ? segs : [{ text: text ?? '' }]);
  const initial = {
    zh: { title: seed(value.titleSegments, value.title), sub: seed(value.subheadingSegments, value.subheading) },
    en: { title: seed(value.en?.titleSegments, value.en?.title), sub: seed(value.en?.subheadingSegments, value.en?.subheading) },
  };

  // Enter and paste already stop at TITLE_MAX_LINES; this catches a title that
  // was stored with more lines before the cap.
  const [titleTooLong, setTitleTooLong] = useState(false);

  const publish = () => {
    const titleSegs = editedSegments(refs.zh.title);
    const subSegs = editedSegments(refs.zh.sub);
    const enTitle = editedSegments(refs.en.title);
    const enSub = editedSegments(refs.en.sub);
    if (lineCount(titleSegs) > TITLE_MAX_LINES || lineCount(enTitle) > TITLE_MAX_LINES) {
      setTitleTooLong(true);
      return;
    }
    const joined = (segs: TitleSegment[]) => segs.map((s) => s.text).join('');
    onPublish(
      withoutEmptyEnglish({
        titleSegments: titleSegs,
        subheadingSegments: subSegs,
        // Keep the flat strings in sync for the renderer's fallback + previews.
        title: joined(titleSegs),
        subheading: joined(subSegs),
        en: { titleSegments: enTitle, title: joined(enTitle), subheadingSegments: enSub, subheading: joined(enSub) },
      }),
    );
  };

  return (
    <SpaceBetween size="l">
      <EditorHead title={t('homepageEditor.part', { n: 1 })} />
      <EnglishHint />
      <BilingualField
        label={t('homepageEditor.pageHeader')}
        hint={t('homepageEditor.maxWordsLines', { n: 20, lines: TITLE_MAX_LINES })}
        error={titleTooLong ? t('homepageEditor.tooManyLines', { n: TITLE_MAX_LINES }) : undefined}
        zh={<RichTextField initial={initial.zh.title} editableRef={refs.zh.title} multiline maxLines={TITLE_MAX_LINES} onInput={() => setTitleTooLong(false)} defaultWeight="bold" seedColor="#0097a7" baseColor="#0f141a" />}
        en={<RichTextField initial={initial.en.title} editableRef={refs.en.title} multiline maxLines={TITLE_MAX_LINES} onInput={() => setTitleTooLong(false)} defaultWeight="bold" seedColor="#0097a7" baseColor="#0f141a" />}
      />
      <BilingualField
        label={t('homepageEditor.subheading')}
        hint={t('homepageEditor.maxWords', { n: 40 })}
        zh={<RichTextField initial={initial.zh.sub} editableRef={refs.zh.sub} multiline defaultWeight="regular" seedColor="#656871" baseColor="#656871" />}
        en={<RichTextField initial={initial.en.sub} editableRef={refs.en.sub} multiline defaultWeight="regular" seedColor="#656871" baseColor="#656871" />}
      />
      <EditorFooter onCancel={onCancel} onPublish={publish} />
    </SpaceBetween>
  );
}

// ── Badge checkboxes (shared Part 2 / Part 3) ───────────────────────────────

function BadgeChecks({ badges, onChange }: { badges: HomepageBadge[]; onChange: (b: HomepageBadge[]) => void }) {
  const toggle = (b: HomepageBadge, on: boolean) =>
    onChange(on ? [...badges.filter((x) => x !== b), b] : badges.filter((x) => x !== b));
  const { t } = useTranslation('settings');
  return (
    <div className="rbms-ewh-badges">
      <Box variant="span" fontWeight="bold">{t('homepageEditor.badge')}</Box>
      <Checkbox checked={badges.includes('NEW')} onChange={({ detail }) => toggle('NEW', detail.checked)}>{t('homepageEditor.badgeNew')}</Checkbox>
      <Checkbox checked={badges.includes('HOT')} onChange={({ detail }) => toggle('HOT', detail.checked)}>{t('homepageEditor.badgeHot')}</Checkbox>
    </div>
  );
}

/** Blue-outline pill "+ Add …" button (Figma), shared by Part 2 / Part 3. */
function AddButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className="rbms-ewh-addbtn" onClick={onClick} disabled={disabled}>
      <span aria-hidden="true">+</span> {label}
    </button>
  );
}

/** Blue-outline round "−" remove button (Figma). */
function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="rbms-ewh-remove" aria-label={label} onClick={onClick}>
      −
    </button>
  );
}

/** Up / down buttons that move a row one place (Part 2 / Part 3). The homepage
 *  renders part2 / part3 in array order, so the row order here is the order
 *  users see. Up is disabled on the first row, down on the last. */
function MoveButtons({
  upLabel,
  downLabel,
  onUp,
  onDown,
}: {
  upLabel: string;
  downLabel: string;
  onUp?: () => void;
  onDown?: () => void;
}) {
  return (
    <>
      <button type="button" className="rbms-ewh-move" aria-label={upLabel} onClick={onUp} disabled={!onUp}>
        ↑
      </button>
      <button type="button" className="rbms-ewh-move" aria-label={downLabel} onClick={onDown} disabled={!onDown}>
        ↓
      </button>
    </>
  );
}

/**
 * Drag-to-reorder for Part 2 / Part 3 rows (@dnd-kit/sortable). Only the handle
 * starts a drag, so typing and selecting text in a row's fields still works.
 * The up / down buttons stay: they are the one-click and screen-reader path,
 * and the keyboard can also drag (focus the handle, Space, arrows, Space).
 * SortableRows owns the list spacing because SpaceBetween only spaces its
 * direct children, which the dnd-kit providers would otherwise hide.
 */
function SortableRows({
  ids,
  onMove,
  children,
}: {
  ids: string[];
  onMove: (from: number, to: number) => void;
  children: ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from >= 0 && to >= 0) onMove(from, to);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <SpaceBetween size="l">{children}</SpaceBetween>
      </SortableContext>
    </DndContext>
  );
}

/** The row's drag handle, wired by the enclosing SortableRow. */
const DragHandleContext = createContext<ReactNode>(null);
function DragHandle() {
  return <>{useContext(DragHandleContext)}</>;
}

function SortableRow({ id, handleLabel, children }: { id: string; handleLabel: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const handle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className="rbms-ewh-drag"
      {...attributes}
      {...listeners}
      aria-label={handleLabel}
    >
      ⋮⋮
    </button>
  );
  return (
    <div
      ref={setNodeRef}
      className={`rbms-ewh-row${isDragging ? ' rbms-ewh-row--dragging' : ''}`}
      // Translate, not Transform: rows differ in height and must not be scaled.
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <DragHandleContext.Provider value={handle}>{children}</DragHandleContext.Provider>
    </div>
  );
}

/** Swap row `i` with its neighbour `i + delta`; out of range leaves rows as-is. */
function moveRow<T>(rows: T[], i: number, delta: -1 | 1): T[] {
  const j = i + delta;
  if (i < 0 || j < 0 || j >= rows.length) return rows;
  const next = [...rows];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

// ── Part 2 ────────────────────────────────────────────────────────────────

export function Part2Editor({
  value,
  onCancel,
  onPublish,
}: {
  value: HotQuestion[];
  onCancel: () => void;
  onPublish: (v: HotQuestion[]) => void;
}) {
  const { t } = useTranslation('settings');
  const [rows, setRows] = useState<HotQuestion[]>(value);
  const names = useBilingualLabels();
  const update = (id: string, patch: Partial<HotQuestion>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id: string) => setRows((rs) => rs.filter((r) => r.id !== id));
  const move = (i: number, delta: -1 | 1) => setRows((rs) => moveRow(rs, i, delta));
  const reorder = (from: number, to: number) => setRows((rs) => arrayMove(rs, from, to));
  // New rows are inserted at the top (first position).
  const add = () =>
    setRows((rs) => (rs.length >= MAX_ITEMS ? rs : [{ id: newQuestionId(), uiCopy: '', prompt: '', badges: [] }, ...rs]));

  return (
    <SpaceBetween size="l">
      <EditorHead
        title={t('homepageEditor.part', { n: 2 })}
        action={<AddButton label={t('homepageEditor.addQuestion')} onClick={add} disabled={rows.length >= MAX_ITEMS} />}
      />
      <EnglishHint />
      <SortableRows ids={rows.map((r) => r.id)} onMove={reorder}>
      {rows.map((row, i) => (
        <SortableRow
          key={row.id}
          id={row.id}
          handleLabel={t('homepageEditor.dragQuestion', { n: i + 1 })}
        >
          <div className="rbms-ewh-row__head">
            <span className="rbms-ewh-row__lead">
              <DragHandle />
              <span className="rbms-ewh-row__title">{t('homepageEditor.question', { n: i + 1 })}</span>
            </span>
            <div className="rbms-ewh-actions">
              <BadgeChecks badges={row.badges ?? []} onChange={(b) => update(row.id, { badges: b })} />
              <MoveButtons
                upLabel={t('homepageEditor.moveQuestionUp', { n: i + 1 })}
                downLabel={t('homepageEditor.moveQuestionDown', { n: i + 1 })}
                onUp={i > 0 ? () => move(i, -1) : undefined}
                onDown={i < rows.length - 1 ? () => move(i, 1) : undefined}
              />
              <RemoveButton label={t('homepageEditor.removeQuestion', { n: i + 1 })} onClick={() => remove(row.id)} />
            </div>
          </div>
          <BilingualField
            label={t('homepageEditor.uiCopy')}
            hint={t('homepageEditor.maxWords', { n: 20 })}
            zh={<Input value={row.uiCopy} onChange={({ detail }) => update(row.id, { uiCopy: detail.value })} placeholder={t('homepageEditor.uiCopyPlaceholder')} ariaLabel={names(t('homepageEditor.uiCopy')).zh} />}
            en={
              <Input
                value={row.en?.uiCopy ?? ''}
                onChange={({ detail }) => update(row.id, { en: { ...row.en, uiCopy: detail.value } })}
                placeholder={row.uiCopy}
                ariaLabel={names(t('homepageEditor.uiCopy')).en}
              />
            }
          />
          <BilingualField
            label={t('homepageEditor.prompt')}
            hint={t('homepageEditor.maxWords', { n: 200 })}
            zh={<Textarea value={row.prompt} onChange={({ detail }) => update(row.id, { prompt: detail.value })} rows={3} ariaLabel={names(t('homepageEditor.prompt')).zh} />}
            en={
              <Textarea
                value={row.en?.prompt ?? ''}
                onChange={({ detail }) => update(row.id, { en: { ...row.en, prompt: detail.value } })}
                placeholder={row.prompt}
                rows={3}
                ariaLabel={names(t('homepageEditor.prompt')).en}
              />
            }
          />
        </SortableRow>
      ))}
      </SortableRows>
      <EditorFooter onCancel={onCancel} onPublish={() => onPublish(rows.map(withoutEmptyEnglish))} />
    </SpaceBetween>
  );
}

// ── Icon upload validation ──────────────────────────────────────────────────
// Client-side pre-check for a nicer UX (instant feedback before uploading). The
// Gateway is still the authority — it re-checks type (via magic bytes) and size
// on POST, and does NOT check pixel dimensions, so that part is UI-only.
const ICON_TYPES = ['image/png', 'image/jpeg', 'image/webp']; // raster only; SVG is rejected server-side (XSS)
const ICON_MAX_BYTES = 512 * 1024; // 512 KB — must match the Gateway cap
const ICON_MIN_SIDE = 64; // reject icons smaller than 64×64 (too blurry in the card)

/** A validation result: `error` blocks the upload, `warn` still allows it. The
 *  message is a key under homepageEditor.icon, translated when rendered. */
type IconIssue = {
  level: 'error' | 'warn';
  key: 'type' | 'tooLarge' | 'unreadable' | 'tooSmall' | 'notSquare' | 'uploadFailed';
  values?: Record<string, string | number>;
};

/**
 * Decode the picked file to read its intrinsic pixel dimensions.
 * Resolves to `{ w, h }`, or `null` if the browser can't decode it (corrupt /
 * not really an image). Uses an object URL that is always revoked to avoid leaks.
 */
function readImageSize(file: File): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * Validate a picked icon file against type / size / dimension rules.
 * Returns:
 *   - an `error` issue  → invalid, caller must NOT upload;
 *   - a `warn` issue    → acceptable but not ideal (non-square), caller may upload;
 *   - `null`            → all good.
 */
async function validateIcon(file: File): Promise<IconIssue | null> {
  // 1) MIME type — only the three raster formats the Gateway accepts.
  if (!ICON_TYPES.includes(file.type)) {
    return { level: 'error', key: 'type' };
  }
  // 2) File size — keep in sync with the Gateway's 512 KB limit.
  if (file.size > ICON_MAX_BYTES) {
    return { level: 'error', key: 'tooLarge', values: { kb: (file.size / 1024).toFixed(0) } };
  }
  // 3) Must actually decode as an image.
  const dim = await readImageSize(file);
  if (!dim) {
    return { level: 'error', key: 'unreadable' };
  }
  // 4) Minimum resolution — avoid a blurry icon in the card.
  if (dim.w < ICON_MIN_SIDE || dim.h < ICON_MIN_SIDE) {
    return { level: 'error', key: 'tooSmall', values: { w: dim.w, h: dim.h, min: ICON_MIN_SIDE } };
  }
  // 5) Aspect ratio — the card icon slot is square, so a far-from-square image
  //    will be cropped. Warn but still allow it.
  const ratio = dim.w / dim.h;
  if (ratio < 0.8 || ratio > 1.25) {
    return { level: 'warn', key: 'notSquare', values: { w: dim.w, h: dim.h } };
  }
  return null;
}

/** Upload an icon to the Gateway; returns the persistent (https) URL + name.
 *  multipart/form-data — do NOT set Content-Type (the browser adds the boundary). */
async function uploadIcon(agent: string, file: File): Promise<{ iconKey: string; iconFileName: string }> {
  const form = new FormData();
  form.append('agent', agent);
  form.append('file', file);
  const resp = await fetch(`${appConfig.gatewayUrl}/channels/portal/workspace-homepage/icon`, {
    method: 'POST',
    headers: gatewayAuthHeaders(), // X-Id-Token etc.; no Content-Type
    body: form,
  });
  if (!resp.ok) throw new Error(`upload failed (${resp.status})`);
  const d = await resp.json();
  // Store the bare S3 key (portable across stages); the URL is built at render
  // time via resolveIconSrc using the current stage's gateway.
  return { iconKey: d.iconKey as string, iconFileName: (d.filename as string) ?? file.name };
}

/** Icon uploader matching Figma: a blue-outline "Choose files" pill + a
 *  light-blue file card (thumbnail / name / size / date + blue ✕). Picks a file,
 *  validates it, uploads to the Gateway, and reports the persistent iconUrl. */
function IconUpload({
  agent,
  fileName,
  iconUrl,
  onUploaded,
  onClear,
}: {
  agent: string;
  fileName?: string;
  iconUrl?: string;
  onUploaded: (iconUrl: string, iconFileName: string) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation('settings');
  const inputRef = useRef<HTMLInputElement>(null);
  const [meta, setMeta] = useState<{ size: number; date: number } | null>(null);
  const [issue, setIssue] = useState<IconIssue | null>(null);
  const [uploading, setUploading] = useState(false);

  // For a pre-existing icon (built-in /icons/*.svg or a stored URL) there's no
  // local File, so read size/date from the asset's HEAD headers (Content-Length
  // / Last-Modified). Skipped for fresh uploads (blob:) — meta is already set.
  useEffect(() => {
    const src = resolveIconSrc(iconUrl);
    if (meta || !src) return;
    let cancelled = false;
    fetch(src, { method: 'HEAD' })
      .then((r) => {
        if (cancelled || !r.ok) return;
        const len = r.headers.get('content-length');
        const lm = r.headers.get('last-modified');
        setMeta({ size: len ? Number(len) : 0, date: lm ? Date.parse(lm) : 0 });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [iconUrl, meta]);
  return (
    <div>
      <button
        type="button"
        className="rbms-ewh-choose"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 15V4" />
          <path d="M7 9l5-5 5 5" />
          <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
        </svg>
        {uploading ? t('homepageEditor.uploading') : t('homepageEditor.chooseFiles')}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = ''; // reset so picking the same file again re-fires onChange
          if (!f) return;
          const found = await validateIcon(f);
          if (found?.level === 'error') {
            // Invalid: show the error and stop — do not upload the file.
            setIssue(found);
            return;
          }
          // Valid (or only a non-blocking warning): upload to the Gateway, then
          // report the persistent https URL. Size/date come from the local File.
          setIssue(found);
          setUploading(true);
          try {
            const { iconKey, iconFileName } = await uploadIcon(agent, f);
            setMeta({ size: f.size, date: f.lastModified });
            onUploaded(iconKey, iconFileName);
          } catch {
            setIssue({ level: 'error', key: 'uploadFailed' });
          } finally {
            setUploading(false);
          }
        }}
      />
      {issue ? (
        <div className={`rbms-ewh-file-msg rbms-ewh-file-msg--${issue.level}`}>
          {t(`homepageEditor.icon.${issue.key}`, issue.values)}
        </div>
      ) : null}
      {fileName || iconUrl ? (
        <div className="rbms-ewh-file">
          <span className="rbms-ewh-file__thumb">{iconUrl ? <img src={resolveIconSrc(iconUrl)} alt="" /> : null}</span>
          <div className="rbms-ewh-file__meta">
            {/* Uploaded file name, else derive a display name from the icon URL
                (built-in icons like /icons/target-attainment.svg). */}
            <div className="rbms-ewh-file__name">{fileName ?? iconUrl?.split('/').pop()}</div>
            {meta && meta.size > 0 ? (
              <div className="rbms-ewh-file__sub">{(meta.size / 1024).toFixed(2)} KB</div>
            ) : null}
            {meta && meta.date > 0 ? (
              <div className="rbms-ewh-file__sub">{new Date(meta.date).toISOString().slice(0, 19)}</div>
            ) : null}
          </div>
          <button type="button" className="rbms-ewh-file__x" aria-label={t('homepageEditor.removeFile')} onClick={() => { setMeta(null); onClear(); }}>
            ×
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ── Part 3 ────────────────────────────────────────────────────────────────

/** Whether a topic's Link is set but not an allowed href (empty is valid). */
function hrefInvalid(href?: string): boolean {
  const h = href?.trim();
  return !!h && !isAllowedHref(h);
}

/** A Link makes the card navigate; an empty Link clears it back to a chat card. */
function withLinkAction(t: HotTopic): HotTopic {
  const href = t.href?.trim();
  return href ? { ...t, href, action: 'navigate' } : { ...t, href: undefined, action: undefined };
}

export function Part3Editor({
  value,
  agent,
  onCancel,
  onPublish,
}: {
  value: HotTopic[];
  agent: string;
  onCancel: () => void;
  onPublish: (v: HotTopic[]) => void;
}) {
  const { t } = useTranslation('settings');
  const [rows, setRows] = useState<HotTopic[]>(value);
  const names = useBilingualLabels();
  const update = (id: string, patch: Partial<HotTopic>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id: string) => setRows((rs) => rs.filter((r) => r.id !== id));
  const move = (i: number, delta: -1 | 1) => setRows((rs) => moveRow(rs, i, delta));
  const reorder = (from: number, to: number) => setRows((rs) => arrayMove(rs, from, to));
  // New rows are inserted at the top (first position).
  const add = () =>
    setRows((rs) =>
      rs.length >= MAX_ITEMS
        ? rs
        : [{ id: newTopicId(), title: '', description: '', prompt: '', badges: [] }, ...rs],
    );

  return (
    <SpaceBetween size="l">
      <EditorHead
        title={t('homepageEditor.part', { n: 3 })}
        action={<AddButton label={t('homepageEditor.addTopic')} onClick={add} disabled={rows.length >= MAX_ITEMS} />}
      />
      <EnglishHint />
      <SortableRows ids={rows.map((r) => r.id)} onMove={reorder}>
      {rows.map((row, i) => (
        <SortableRow
          key={row.id}
          id={row.id}
          handleLabel={t('homepageEditor.dragTopic', { n: i + 1 })}
        >
          <div className="rbms-ewh-row__head">
            <span className="rbms-ewh-row__lead">
              <DragHandle />
              <span className="rbms-ewh-row__title">{t('homepageEditor.topic', { n: i + 1 })}</span>
            </span>
            <div className="rbms-ewh-actions">
              <BadgeChecks badges={row.badges ?? []} onChange={(b) => update(row.id, { badges: b })} />
              <MoveButtons
                upLabel={t('homepageEditor.moveTopicUp', { n: i + 1 })}
                downLabel={t('homepageEditor.moveTopicDown', { n: i + 1 })}
                onUp={i > 0 ? () => move(i, -1) : undefined}
                onDown={i < rows.length - 1 ? () => move(i, 1) : undefined}
              />
              <RemoveButton label={t('homepageEditor.removeTopic', { n: i + 1 })} onClick={() => remove(row.id)} />
            </div>
          </div>
          <Field label={t('homepageEditor.iconImage')}>
            <IconUpload
              agent={agent}
              fileName={row.iconFileName}
              iconUrl={row.iconUrl}
              onUploaded={(url, name) => update(row.id, { iconUrl: url, iconFileName: name })}
              onClear={() => update(row.id, { iconFileName: undefined, iconUrl: undefined })}
            />
          </Field>
          <BilingualField
            label={t('homepageEditor.topicTitle')}
            hint={t('homepageEditor.maxWords', { n: 20 })}
            zh={<Input value={row.title} onChange={({ detail }) => update(row.id, { title: detail.value })} placeholder={t('homepageEditor.topicTitlePlaceholder')} ariaLabel={names(t('homepageEditor.topicTitle')).zh} />}
            en={
              <Input
                value={row.en?.title ?? ''}
                onChange={({ detail }) => update(row.id, { en: { ...row.en, title: detail.value } })}
                placeholder={row.title}
                ariaLabel={names(t('homepageEditor.topicTitle')).en}
              />
            }
          />
          <BilingualField
            label={t('homepageEditor.description')}
            hint={t('homepageEditor.maxWords', { n: 20 })}
            zh={<Textarea value={row.description} onChange={({ detail }) => update(row.id, { description: detail.value })} rows={2} ariaLabel={names(t('homepageEditor.description')).zh} />}
            en={
              <Textarea
                value={row.en?.description ?? ''}
                onChange={({ detail }) => update(row.id, { en: { ...row.en, description: detail.value } })}
                placeholder={row.description}
                rows={2}
                ariaLabel={names(t('homepageEditor.description')).en}
              />
            }
          />
          <BilingualField
            label={t('homepageEditor.prompt')}
            hint={t('homepageEditor.maxWords', { n: 200 })}
            zh={<Textarea value={row.prompt ?? ''} onChange={({ detail }) => update(row.id, { prompt: detail.value })} rows={3} ariaLabel={names(t('homepageEditor.prompt')).zh} />}
            en={
              <Textarea
                value={row.en?.prompt ?? ''}
                onChange={({ detail }) => update(row.id, { en: { ...row.en, prompt: detail.value } })}
                placeholder={row.prompt}
                rows={3}
                ariaLabel={names(t('homepageEditor.prompt')).en}
              />
            }
          />
          <Field
            label={t('homepageEditor.link')}
            optional
            hint={t('homepageEditor.linkHint')}
            error={hrefInvalid(row.href) ? t('homepageEditor.linkError') : undefined}
          >
            <Input
              value={row.href ?? ''}
              onChange={({ detail }) => update(row.id, { href: detail.value })}
              invalid={hrefInvalid(row.href)}
              placeholder="/cheetah"
              ariaLabel={t('homepageEditor.linkLabel', { n: i + 1 })}
            />
          </Field>
        </SortableRow>
      ))}
      </SortableRows>
      <EditorFooter
        onCancel={onCancel}
        onPublish={() => onPublish(rows.map(withLinkAction).map(withoutEmptyEnglish))}
        publishDisabled={rows.some((r) => hrefInvalid(r.href))}
      />
    </SpaceBetween>
  );
}
